const FIRST_VISIT_DELAY = 30000; // 30 seconds
const QR_VISIT_DELAY = 100; // 0.1 seconds

const toursPublicPagesFlag = document.body.getAttribute('data-enable-tours-public-pages');
const enableToursPublicPages = toursPublicPagesFlag === null
    ? window.enableToursPublicPages !== false
    : toursPublicPagesFlag !== 'false';
const isTourPage = enableToursPublicPages && document.body.getAttribute('data-is-tour-page') === 'true';
const isCashPage = document.body.getAttribute('data-is-cash-page') === 'true';
const isTourBookingLink = enableToursPublicPages && document.body.getAttribute('data-is-tour-booking-link') === 'true';
const isCareersPage = document.body.getAttribute('data-is-careers-page') === 'true';
const isZahsPage = document.body.getAttribute('data-is-school-page') === 'true';
const isZahsApplicationPage = document.body.getAttribute('data-is-school-application-page') === 'true';
const isPaymentStatusPage = document.body.getAttribute('data-is-payment-status-page') === 'true';
const isSubscriptionCheckoutPage = document.body.getAttribute('data-is-subscription-checkout-page') === 'true';
const disableAutoContactModal =
    document.body.getAttribute('data-disable-auto-contact-modal') === 'true';
const disableNewsToast =
    document.body.getAttribute('data-disable-news-toast') === 'true';

import { bindContactFormSubmission, contactThankYouURL } from './modules/contactFormSecurity';
import Modal from 'bootstrap/js/dist/modal';
import Toast from 'bootstrap/js/dist/toast';
import Collapse from 'bootstrap/js/dist/collapse';
import Carousel from 'bootstrap/js/dist/carousel';
import Dropdown from 'bootstrap/js/dist/dropdown';
import Tab from 'bootstrap/js/dist/tab';
import { showNotificationInTitle, removeNotificationFromTitle } from './modules/titleNotifications';
import * as VisitorTracking from './modules/visitorTracking';
import * as DynamicContentLoader from './modules/dynamicContentLoader';
import { getIntlTelInputLoadedStatus } from './modules/dynamicContentLoader';
import * as Utils from './modules/utils';
import * as TourDisplay from './modules/tourDisplay';
import * as JobDisplay from './modules/jobsDisplay';
import * as Scroll from './modules/scroll';
import * as ResizeListenUpdates from './modules/resizeListenUpdates';
import * as GoogleTracking from './modules/googleTracking';
import { trackLeadCreated } from "./modules/openaiAdsTracking";
import * as EnrollNow from './modules/account/enrollNow';
import * as Schedule from './modules/appointments/schedule';
import * as Translator from './modules/translateThroughClick';
import { translateText } from './modules/translator';
import * as StoreProducts from './modules/storeProducts';
import { initBlogSlider } from './modules/blogSlider';
import { initZahsLastInterest } from './modules/zahsLastInterest.js';
import { initApplicationForm, validateEmail } from "./modules/applicationForm";
import { initApplicationPayment } from "./modules/applicationPayment";
import {floatingBarInit} from "./modules/zahsFloatingBar";
import { initChatPlatformBadge } from './modules/animations/chatPlatformBadge.js';
import * as MarketingAttribution from './modules/marketingAttribution.js';
import { initCareerFair, isCareerFairLink } from './modules/careerFair.js';
import { setConsentDisclosure } from './modules/progressiveDisclosure.js';

//import AOS library
import AOS from 'aos';
import 'aos/dist/aos.css';
AOS.init({
  once: true,      // Animate only once
  duration: 600,   // Animation duration in ms
  offset: 80,      // Offset (in px) from the original trigger point
});

window.Modal = Modal;
window.bootstrap = {
    Modal: Modal,
    Toast: Toast,
    Collapse: Collapse,
    Carousel: Carousel,
    Dropdown: Dropdown,
    Tab: Tab,
};

const CAP_TOKEN_TTL_MS = 110000;
let zoniModalCapClient = null;
let zoniModalCapInFlight = null;
let zoniModalCapSolvedAt = 0;

function normalizeCapToken(value) {
    if (typeof value === 'string') {
        return value.trim();
    }
    if (value && typeof value === 'object') {
        if (typeof value.detail?.token === 'string') {
            return value.detail.token.trim();
        }
        if (typeof value.token === 'string') {
            return value.token.trim();
        }
    }
    return '';
}

function getZoniModalCapApiEndpoint() {
    const endpointInput = document.getElementById('zoni-modal-cap-api-endpoint');
    return endpointInput ? String(endpointInput.value || '').trim() : '';
}

function setZoniModalCapToken(value) {
    const tokenInput = document.getElementById('zoni-modal-cap-token');
    const token = normalizeCapToken(value);
    if (tokenInput) {
        tokenInput.value = token;
    }
    zoniModalCapSolvedAt = token ? Date.now() : 0;
}

function getZoniModalCapToken() {
    const tokenInput = document.getElementById('zoni-modal-cap-token');
    return tokenInput ? String(tokenInput.value || '').trim() : '';
}

function hasFreshZoniModalCapToken() {
    const token = getZoniModalCapToken();
    if (!token) {
        return false;
    }
    return Date.now() - zoniModalCapSolvedAt < CAP_TOKEN_TTL_MS;
}

function isZoniModalCapEnabled() {
    return Boolean(getZoniModalCapApiEndpoint());
}

function getOrCreateZoniModalCapClient() {
    const apiEndpoint = getZoniModalCapApiEndpoint();
    if (!apiEndpoint) {
        return null;
    }
    if (!window.Cap) {
        return null;
    }
    if (!zoniModalCapClient || zoniModalCapClient.__apiEndpoint !== apiEndpoint) {
        zoniModalCapClient = new window.Cap({ apiEndpoint });
        zoniModalCapClient.__apiEndpoint = apiEndpoint;
    }
    return zoniModalCapClient;
}

async function solveZoniModalCapToken(force = false) {
    if (!isZoniModalCapEnabled()) {
        return '';
    }

    if (!force && hasFreshZoniModalCapToken()) {
        return getZoniModalCapToken();
    }

    if (zoniModalCapInFlight) {
        return zoniModalCapInFlight;
    }

    zoniModalCapInFlight = (async () => {
        const capClient = getOrCreateZoniModalCapClient();
        if (!capClient) {
            throw new Error("CAP client is not available");
        }
        const solveResult = await capClient.solve();
        const token = normalizeCapToken(solveResult?.token || capClient.token);
        if (!token) {
            throw new Error("CAP solve did not return a token");
        }
        setZoniModalCapToken(token);
        return token;
    })();

    try {
        return await zoniModalCapInFlight;
    } finally {
        zoniModalCapInFlight = null;
    }
}

function primeZoniModalCapToken() {
    if (!isZoniModalCapEnabled() || hasFreshZoniModalCapToken() || zoniModalCapInFlight) {
        return;
    }
    solveZoniModalCapToken(false).catch((err) => {
        console.debug("CAP pre-solve failed:", err);
    });
}


$('#showConfirmPassword').on('click', function(e){
    e.preventDefault();
    if($('#icon2').hasClass('fa-eye-slash')){
        $('#icon2').removeClass('fa-eye-slash').addClass('fa-eye');
        $('#confirmPassword').attr('type', 'text');
  
    }
    else{
        $('#icon2').removeClass('fa-eye').addClass('fa-eye-slash'); 
        $('#confirmPassword').attr('type', 'password');
    
    }

})
$('#showPassword').on('click', function(e){
    e.preventDefault();
    if($('#icon1').hasClass('fa-eye-slash')){
        $('#icon1').removeClass('fa-eye-slash').addClass('fa-eye');
        $('#password').attr('type', 'text');
       
    }
    else{
        $('#icon1').removeClass('fa-eye').addClass('fa-eye-slash'); 
        $('#password').attr('type', 'password');
      
    }

})

const qr_utm = VisitorTracking.hasMatchingQr();

// Skip all auto-modal logic on pages that opt out of the contact modal
const skipAutoModal =
    disableAutoContactModal || isPaymentStatusPage || isSubscriptionCheckoutPage || isCareerFairLink();

initCareerFair({ skipAutoModal: disableAutoContactModal || isPaymentStatusPage || isSubscriptionCheckoutPage });

if (!skipAutoModal && VisitorTracking.hasMatchingQrAndIsScanSource()) {

    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModal() || !modalElement) {
            return;
        }

        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');

    }, QR_VISIT_DELAY);
} else if (!skipAutoModal && VisitorTracking.hasMatchingQRisSchoolClosed()) {
    VisitorTracking.customizeModalSchoolCloseWhatsApp();

    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModal() || !modalElement) {
            return;
        }

        VisitorTracking.customizeModalSchoolCloseWhatsApp;
        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');
    }, QR_VISIT_DELAY);
} else if (!skipAutoModal && VisitorTracking.hasMatchingQRisOrlandoPromo()) {
    VisitorTracking.customizeModalOrlandoPromoWhatsApp();

    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModal() || !modalElement) {
            return;
        }

        VisitorTracking.customizeModalOrlandoPromoWhatsApp;
        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');
    }, QR_VISIT_DELAY);
} else if (!skipAutoModal && VisitorTracking.hasMatchingQRisTampaPromo()) {
    VisitorTracking.customizeModalTampaPromoWhatsApp();

    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModal() || !modalElement) {
            return;
        }

        VisitorTracking.customizeModalTampaPromoWhatsApp;
        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');
    }, QR_VISIT_DELAY);
} else if (!skipAutoModal && VisitorTracking.isFirstTimeVisitor() && !isTourPage && !isCareersPage && !isZahsApplicationPage) {
    VisitorTracking.customizeModalForFirstTimeVisitor();

    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModalOrOldVisitor() || !modalElement) {
            return;
        }

        VisitorTracking.customizeModalForFirstTimeVisitor();
        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');

    }, FIRST_VISIT_DELAY);  // FIRST_VISIT_DELAY milliseconds = 30 seconds
} else if (!skipAutoModal && enableToursPublicPages && VisitorTracking.hasMatchingToursQrUTMParameters()) {
    VisitorTracking.customizeModalForToursQrUTM();
    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModalOrOldVisitor() || !modalElement) {
            return;
        }

        VisitorTracking.customizeModalForToursQrUTM();
        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');

    }, QR_VISIT_DELAY);  // FIRST_VISIT_DELAY milliseconds = 30 seconds
} else if (!skipAutoModal && VisitorTracking.hasMatchingQrRollupUTMParameters()) {
    VisitorTracking.customizeModalForQrRollupVisitor();
    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModalOrOldVisitor() || !modalElement) {
            return;
        }
        
        VisitorTracking.customizeModalForQrRollupVisitor();
        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');

    }, QR_VISIT_DELAY);  // FIRST_VISIT_DELAY milliseconds = 30 seconds}
} else if (!skipAutoModal && enableToursPublicPages && VisitorTracking.isFirstTimeVisitor() && isTourPage && !isTourBookingLink) {
    VisitorTracking.customizeModalToursPage();
    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModalOrOldVisitor() || !modalElement) {
            return;
        }
        
        VisitorTracking.customizeModalToursPage();
        var bootstrapModal = new Modal(modalElement);
        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');

    }, FIRST_VISIT_DELAY);  // FIRST_VISIT_DELAY milliseconds = 30 seconds}
} else if (!skipAutoModal && VisitorTracking.isFirstTimeVisitor() && isCashPage) {
    VisitorTracking.customizeModalCashPage();
    setTimeout(function () {

        var modalElement = document.getElementById('zoniModal');
        if (VisitorTracking.checkOpenModalOrOldVisitor() || !modalElement) {
            return;
        }
        VisitorTracking.customizeModalCashPage();
        var bootstrapModal = new Modal(modalElement);
        new Modal(modalElement).show();
        showNotificationInTitle();
        localStorage.setItem('isOldVisitor', 'true');

    }, QR_VISIT_DELAY);  // FIRST_VISIT_DELAY milliseconds = 30 seconds}
}

function syncWhatsAppConsentRequirement(isRequired = false) {
    const whatsappConsentEl = document.getElementById('whatsapp_consent');
    const requirementNoteEl = document.getElementById('whatsapp_consent_requirement_note');

    if (!whatsappConsentEl || !requirementNoteEl) {
        return;
    }

    const optionalLabel =
        requirementNoteEl.dataset.optionalLabel || '(Optional)';
    const requiredLabel =
        requirementNoteEl.dataset.requiredLabel
        || '(Required to start a WhatsApp live chat.)';

    requirementNoteEl.textContent = isRequired ? requiredLabel : optionalLabel;

    if (isRequired) {
        whatsappConsentEl.setAttribute('required', 'required');
    } else {
        whatsappConsentEl.removeAttribute('required');
    }
}

function initializeConsentProgressiveDisclosure() {
    const form = document.getElementById('zoni-form');
    const modal = document.getElementById('zoniModal');
    const emailInput = document.getElementById('email');
    const phoneInput = document.getElementById('intl_phone_id');
    const emailSection = document.getElementById('email-marketing-consent-disclosure');
    const phoneSection = document.getElementById('phone-consent-disclosure');

    const syncEmail = () => {
        const hasValidEmail = Boolean(
            emailInput?.value.trim()
            && emailInput.validity.valid
        );
        setConsentDisclosure(emailInput, emailSection, hasValidEmail);
        return hasValidEmail;
    };

    const syncPhone = (knownValidity = null) => {
        const hasValidPhone = typeof knownValidity === 'boolean'
            ? knownValidity
            : Boolean(window.iti?.isValidNumber?.());
        setConsentDisclosure(phoneInput, phoneSection, hasValidPhone);
        return hasValidPhone;
    };

    ['input', 'change', 'blur'].forEach((eventName) => {
        emailInput?.addEventListener(eventName, syncEmail);
    });
    phoneInput?.addEventListener('zoni:phone-validity-change', (event) => {
        syncPhone(Boolean(event.detail?.isValid));
    });
    phoneInput?.addEventListener('input', () => {
        if (!phoneInput.value.trim()) {
            syncPhone(false);
        }
    });
    modal?.addEventListener('shown.bs.modal', () => {
        syncEmail();
        syncPhone();
    });
    form?.addEventListener('reset', () => {
        window.setTimeout(() => {
            syncEmail();
            syncPhone(false);
        }, 0);
    });

    setConsentDisclosure(emailInput, emailSection, false);
    setConsentDisclosure(phoneInput, phoneSection, false);

    return { syncEmail, syncPhone };
}

document.getElementById('zoniModal')?.addEventListener('show.bs.modal', function (event) {
    if (!getIntlTelInputLoadedStatus()) {
        DynamicContentLoader.loadAndInitializeIntlTelInput('#intl_phone_id');
    }
    syncWhatsAppConsentRequirement(false);
    // Check if the modal was triggered by the "Plan your own trip" button
    const triggerElement = event.relatedTarget;
    if (triggerElement && triggerElement.classList.contains('plan-your-trip-btn')) {
        if (!enableToursPublicPages) {
            event.preventDefault();
            return;
        }
        // Call the customizeModalForPlanYourOwnTrip function to adjust the modal content
        VisitorTracking.customizeModalForPlanYourOwnTrip();
    }

    if (triggerElement && triggerElement.classList.contains('order-brochure-btn')) {
        if (!enableToursPublicPages) {
            event.preventDefault();
            return;
        }
        // Call the customizeModalForPlanYourOwnTrip function to adjust the modal content
        VisitorTracking.customizeModalForOrderBrochure();
    }

    if (triggerElement && triggerElement.classList.contains('how-can-we-help-btn')) {
        // Call the customizeModalForPlanYourOwnTrip function to adjust the modal content
        VisitorTracking.customizeModalForPartners();
    }

    if (triggerElement && triggerElement.classList.contains('whatsapp-link')) {
        // Call the customize modal for WhatsApp
        VisitorTracking.customizeModalWhatsApp();
        syncWhatsAppConsentRequirement(true);
    }
    if (triggerElement && triggerElement.classList.contains('telegram-link')) {
        VisitorTracking.customizeModalTelegram();
    }
    if (triggerElement && triggerElement.classList.contains('line-link')) {
        VisitorTracking.customizeModalLine();
    }
    if (triggerElement && triggerElement.classList.contains('sms-link')) {
        VisitorTracking.customizeModalSMS();
    }
    if (triggerElement && triggerElement.classList.contains('btn-header')) {
        VisitorTracking.setModalToDefault();
    }

    primeZoniModalCapToken();
  
});

document.getElementById('zoniModal')?.addEventListener('hidden.bs.modal', function () {
    setZoniModalCapToken('');
    syncWhatsAppConsentRequirement(false);
    if (zoniModalCapClient?.reset) {
        try {
            zoniModalCapClient.reset();
        } catch (err) {
            console.debug("CAP reset failed:", err);
        }
    }
});



$(document).ready(function () {
    if(!getIntlTelInputLoadedStatus()) {
        if (document.querySelector("#intl_phone_id")) {
            DynamicContentLoader.loadAndInitializeIntlTelInput("#intl_phone_id");
        }
    }
    const consentDisclosures = initializeConsentProgressiveDisclosure();
    const primaryEmailEl = document.getElementById('email');
    const emailMarketingConsentEl = document.getElementById('email_marketing_consent');
    const emailMarketingAddressPreview = document.getElementById('email-marketing-address-preview');
    let lastEmailMarketingAddress = primaryEmailEl?.value || '';
    const syncEmailMarketingAddressPreview = () => {
        const requested = Boolean(emailMarketingConsentEl?.checked);
        if (!emailMarketingAddressPreview) return;
        emailMarketingAddressPreview.classList.toggle('d-none', !requested);
        emailMarketingAddressPreview.replaceChildren();
        if (!requested) return;

        const normalizedEmail = GoogleTracking.normalizeEmail(primaryEmailEl?.value || '');
        if (!normalizedEmail || !primaryEmailEl?.checkValidity()) {
            emailMarketingAddressPreview.textContent = emailMarketingAddressPreview.dataset.invalid || '';
            return;
        }

        const template = emailMarketingAddressPreview.dataset.message || '';
        const marker = '{email}';
        const markerIndex = template.indexOf(marker);
        if (markerIndex === -1) {
            emailMarketingAddressPreview.textContent = template;
            return;
        }
        const address = document.createElement('strong');
        address.textContent = normalizedEmail;
        emailMarketingAddressPreview.append(
            document.createTextNode(template.slice(0, markerIndex)),
            address,
            document.createTextNode(template.slice(markerIndex + marker.length))
        );
    };
    emailMarketingConsentEl?.addEventListener('change', syncEmailMarketingAddressPreview);
    const handleEmailMarketingAddressChange = () => {
        const currentAddress = primaryEmailEl?.value || '';
        if (currentAddress !== lastEmailMarketingAddress) {
            if (emailMarketingConsentEl) emailMarketingConsentEl.checked = false;
            lastEmailMarketingAddress = currentAddress;
        }
        syncEmailMarketingAddressPreview();
    };
    ['input', 'change'].forEach((eventName) => {
        primaryEmailEl?.addEventListener(eventName, handleEmailMarketingAddressChange);
    });
    document.getElementById('zoni-form')?.addEventListener('reset', () => {
        window.setTimeout(() => {
            lastEmailMarketingAddress = primaryEmailEl?.value || '';
            if (emailMarketingConsentEl) emailMarketingConsentEl.checked = false;
            syncEmailMarketingAddressPreview();
        }, 0);
    });
    syncEmailMarketingAddressPreview();
    var $video = $("#banner_video");

    if ($video.length) { // Check if element exists
        var windowWidth = $(window).width();

        if (windowWidth < 768) {
            $video.attr('src', $video.data('sm'));
        } else {
            $video.attr('src', $video.data('md'));
        }

        // This line ensures that the video plays after changing the source.
        $video.get(0).load();
    }

    // Browser details
    let userLanguage = navigator.language || navigator.userLanguage;

    const submitBtn = document.getElementById('zoniModalSubmitButton');
    const spinner   = document.getElementById('zoniModalLoadingSpinner');
    const btnText   = document.getElementById('zoniModalSubmitText');

    bindContactFormSubmission(document.getElementById('zoni-form'), submitBtn, async function (e) {

        // Validate the form
        const form = document.getElementById('zoni-form');
        const ok = await validateEmail('email');
        consentDisclosures.syncEmail();

        // Customer-care consent is required worldwide.
        const smsConsentEl = document.getElementById('sms_consent');
        smsConsentEl.setAttribute('required', 'required');
        const whatsappConsentEl = document.getElementById('whatsapp_consent');
        syncWhatsAppConsentRequirement(VisitorTracking.isWhatsAppChat);
        const phoneInputInstance = window.iti;
        const phoneIsValid = Boolean(phoneInputInstance?.isValidNumber?.());
        consentDisclosures.syncPhone(phoneIsValid);

        if (!phoneIsValid) {
            await showAlert('Please enter a valid phone number for the selected country.');
            form.classList.add('was-validated');
            document.getElementById('intl_phone_id')?.focus();
            return;
        }

        if (
            VisitorTracking.isWhatsAppChat
            && whatsappConsentEl
            && !whatsappConsentEl.checked
        ) {
            await showAlert("WhatsApp consent is required to start a WhatsApp chat.");
            form.classList.add('was-validated');
            return;
        }

        if (!form.checkValidity() || !ok) {
            form.reportValidity();
            form.classList.add('was-validated');
            return;
        }

        // Extra guard (clear message)
        if (!smsConsentEl.checked) {
            await showAlert(await translateText("SMS customer-care consent is required."));
            form.classList.add('was-validated');
            return;
        }

        if (!form.checkValidity() || !ok) {
            // If the form is invalid, show validation errors
            form.reportValidity();
            // Add was-validated class to the form
            form.classList.add('was-validated');
            return;
        } else {
            // If the form is valid, remove was-validated class
            form.classList.remove('was-validated');
        }
        let capToken = '';

        // disable and swap to spinner
        if (submitBtn && btnText && spinner) {
            submitBtn.disabled = true;
            btnText.classList.add('d-none');
            spinner.classList.remove('d-none');
        }

        if (isZoniModalCapEnabled()) {
            try {
                capToken = await solveZoniModalCapToken(true);
            } catch (capErr) {
                console.error("CAP solve failed:", capErr);
                await showAlert(await translateText("Captcha verification failed. Please try again."));
                if (submitBtn && btnText && spinner) {
                    spinner.classList.add('d-none');
                    btnText.classList.remove('d-none');
                    submitBtn.disabled = false;
                }
                return;
            }
        }

        if ($('#phone_number').val() || $('#zoni-contact-website').val()) {
            // It's probably a bot, don't submit the form.
            if (submitBtn && btnText && spinner) {
                spinner.classList.add('d-none');
                btnText.classList.remove('d-none');
                submitBtn.disabled = false;
            }
            return;
        }

        // Prepare references outside try/catch
        const mainInterestFlags = VisitorTracking.getMainInterestFlags();
        const productInterestFlags = VisitorTracking.getProductInterestFlags();
        let normalizedPhone;
        let normalizedEmail;
        let formData = {};


        try {
            const fullNumber = phoneInputInstance.getNumber();
            normalizedPhone = GoogleTracking.normalizePhoneNumber(fullNumber);
            normalizedEmail = GoogleTracking.normalizeEmail($('#email').val());
            const emailMarketingConsentRequested = Boolean(emailMarketingConsentEl?.checked);
            let personTypeCheckboxes = VisitorTracking.mapPersonTypeToCheckboxes($('#person_type').val());
            const marketingAttribution = MarketingAttribution.getMarketingAttribution();
            const gclid = marketingAttribution?.latest_touch?.gclid || '';

        let message = $('#message').val();
        if (isCashPage) {
            message = message + "\n I am claiming my $25 OFF in my registration fee.";
        }

            formData = {
                full_name: $('#full_name').val(),
                email: normalizedEmail,
                message: message,
                student_name: $('#student_name').val(),
                phone: normalizedPhone,
                sms_consent: $('#sms_consent').is(':checked') ? 1 : 0,
                sms_consent_marketing: $('#sms_consent_marketing').is(':checked') ? 1 : 0,
                whatsapp_consent: $('#whatsapp_consent').is(':checked') ? 1 : 0,
                language: userLanguage,
                url_language_code: $('input[name="url_language_code"]').val(),
                referrer_url: document.referrer || 'Direct',  // if referrer is not available, mark it as 'Direct'
                google_click_identifier: gclid || '', // Use the extracted gclid value or an empty string if it's not available
                ...(marketingAttribution ? { marketing_attribution: marketingAttribution } : {}),
                visited_before: VisitorTracking.visitedBefore,
                referrer_name: $('#referrer_name').val(),
                referrer_email: $('#referrer_email').val(),
                referrer_birth_date: $('#referrer_birth_date').val(),
                referrer_student_id: $('#referrer_student_id').val(),
                destination: $('#destination').val(),
                departure_city: $('#departure_city').val(),
                school_or_organization: $('#school_or_organization').val(),
                zoni_american_high_school: VisitorTracking.isAmericanHighSchoolInterest,
                zoni_american_high_school_enrollment: VisitorTracking.isAmericanHighSchoolEnrollment,
                parent_name: $('#parent_name').val(),
                order_brochure: VisitorTracking.isOrderBrochure,
                is_whatsapp: VisitorTracking.isWhatsAppChat,
                is_telegram: VisitorTracking.isTelegramChat,
                is_line: VisitorTracking.isLineChat,
                is_sms: VisitorTracking.isSMSChat,
                is_school_closed: VisitorTracking.isSchoolClosed,
                is_orlando_promo: VisitorTracking.isOrlandoPromo,
                is_tampa_promo: VisitorTracking.isTampaPromo,
                ...personTypeCheckboxes,
                
                // === Main Interest flags ===
                learn: mainInterestFlags.learn,
                tour: mainInterestFlags.tour,
                join_us: mainInterestFlags.join_us,
                agency_or_partner: mainInterestFlags.agency_or_partner,
                zoni_ambassadors: mainInterestFlags.zoni_ambassadors,
                
                // === Learn Product Interest flags ===
                ...productInterestFlags,

                zahs_program_interest: $('#zahs_program_interest').val(),
                zahs_track_interest: $('#zahs_track_interest').val(),

                // RAW ROOT PAGE INTEREST (only present on root page)
                lead_interest: $('#lead_interest').val() || null,
            };

            // Consent is sent only to the contact endpoint. It is deliberately
            // excluded from formData because formData is also sent to GTM and
            // later serialized into the Contacts/CRM workflow.
            const submissionData = {
                ...formData,
                email_marketing_consent: emailMarketingConsentRequested ? 1 : 0,
                email_marketing_consent_version:
                    emailMarketingConsentRequested
                        ? String(emailMarketingConsentEl?.dataset.consentVersion || '')
                        : '',
            };

            // Prepare Google Tag Manager Enhanced Conversion Data
            window.dataLayer.push({
                'event': 'set_user_data',
                'phone_number': normalizedPhone,
                ...formData
            });

            const response = await $.ajax({
                method: "POST",
                url: "/api/method/zoni_edu.zoni_edu.doctype.contacts.contact_submission.submit_contact_form",
                data: JSON.stringify({
                    data: submissionData,
                    honeypot: $('#zoni-contact-website').val() || $('#phone_number').val() || ''
                }),
                dataType: "json",
                contentType: "application/json",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    "X-Frappe-CSRF-Token": frappe.csrf_token,
                    "X-Cap-Token": capToken
                }
            });

            // Handle success
            handleFormSuccess(
                response,
                formData,
                normalizedPhone,
                normalizedEmail,
                emailMarketingConsentRequested
            );

        } catch (err) {
            handleFormError(err, normalizedPhone, formData);
        }
    }, async function () {
        await showAlert(await translateText('Unable to submit. Please try again.'));
    }, function () {
        if (btnText && spinner) {
            spinner.classList.add('d-none');
            btnText.classList.remove('d-none');
        }
    });

    async function getZahsApplicationTokenFromContact(formData) {
        const redirectLang =
            formData.url_language_code || document.documentElement.lang || 'en';
        let capToken = '';
        if (isZoniModalCapEnabled()) {
            try {
                capToken = await solveZoniModalCapToken(true);
            } catch (capErr) {
                console.warn("CAP token unavailable for ZAHS draft creation:", capErr);
            }
        }

        const apiUrl =
            '/api/method/zoni_edu.zoni_lms.doctype.zahs_application.zahs_application.create_draft_from_contact';

        const res = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Frappe-CSRF-Token': frappe.csrf_token,
                'X-Cap-Token': capToken
            },
            credentials: 'include',
            body: JSON.stringify({
                full_name: formData.full_name || '',
                email: formData.email || '',
                phone: formData.phone || '',
                zahs_program_interest: formData.zahs_program_interest || '',
                zahs_track_interest: formData.zahs_track_interest || '',
                url_language_code: redirectLang
            })
        });
        const payload = await res.json();

        if (!res.ok) {
            console.error('Failed to create ZAHS draft from contact:', payload);
            return null;
        }

        const message = payload.message || {};
        if (typeof message === 'string') {
            return { token: message };
        }
        return message;
    }

    async function handleFormSuccess(
        data,
        formData,
        normalizedPhone,
        normalizedEmail,
        emailMarketingConsentRequested
    ) {
        // Only attribution parameters belong in the tracking iframe URL.
        localStorage.setItem('userData', JSON.stringify({
            'email': normalizedEmail,
            'phone_number': normalizedPhone
        }));
        const iframeURL = contactThankYouURL(window.location.search);
        
        VisitorTracking.setEmail(normalizedEmail);
        VisitorTracking.setPhone(normalizedPhone);

        // Set the iframe's src to the "Thank You" page for legacy reasons
        // If you remove the next code line means that the marketing team
        // has updated the Google Ads and Google Analytics procedures
        // and the iframe is not needed anymore
        document.getElementById("thankYouFrame").src = iframeURL;
        
        // Hide all open modals
        var openModals = document.querySelectorAll('.modal.show');
        openModals.forEach(modal => {
            var bootstrapModal = Modal.getInstance(modal);
            bootstrapModal.hide();
        });

        localStorage.setItem('isOldVisitor', 'true'); // Set the isOldVisitor flag in local storage

        // Reset the viewport zoom to default
        $('meta[name="viewport"]').attr('content', 'width=device-width, initial-scale=1, maximum-scale=1');

        $('#zoni-form')[0].reset();     // Reset the form after submission
        removeNotificationFromTitle(); // Remove the notification from the title

        const emailOptInStatus = emailMarketingConsentRequested
            ? String(data?.message?.email_marketing_opt_in_status || '')
            : '';
        await showAlertBasedOnCondition(formData, emailOptInStatus);

        // Push event to GTM
        window.dataLayer.push({
            'event': 'form_success_submission',
            'phone_number': normalizedPhone,
            ...formData
        });

        try {
            // Contacts currently use email in the document name, so use default
            // duplicate protection instead of passing the response docname.
            trackLeadCreated();
        } catch (error) {
            // OpenAI tracking must never affect the form success flow.
        }
    }

    function handleFormError(err, normalizedPhone, formData) {
        let errorMessage = "There was an error. Please try again!";
        try {
            let parsedError = JSON.parse(err.responseText);
            errorMessage = JSON.parse(parsedError._server_messages)[0].message;
        } catch (e) {
            console.error('Error parsing server response:', e);
        }

        showAlert(errorMessage);

        window.dataLayer.push({
            'event': 'form_error_submission',
            'phone_number': normalizedPhone,
            ...formData
        });
    }


    async function showAlertBasedOnCondition(formData, emailOptInStatus = '') {
        const messageContainer = document.getElementById('email-marketing-opt-in-messages');
        let emailOptInNotice = '';
        if (messageContainer && emailOptInStatus) {
            const notice = String(
                messageContainer.dataset[emailOptInStatus.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())]
                || ''
            );
            if (notice) {
                emailOptInNotice = `<hr><p class="mb-0"><strong>${notice}</strong></p>`;
            }
        }
        const showSuccessAlert = (message, options = {}) => showAlert(message, {
            ...options,
            appendHtml: emailOptInNotice,
        });

        if (enableToursPublicPages && VisitorTracking.isOrderBrochure) {
            await showSuccessAlert("Your order brochure request will be processed immediately! Click OK to open the brochure. One of our representatives will contact you soon to offer you printed materials or future assistance. Thank you for your Zoni Tours brochure order!", formData);
        } else if (isCashPage) {
            await showSuccessAlert("Thank you for reaching out to Zoni! Your message has been successfully received. One of our team representatives will be in touch with you soon. We appreciate your interest! Your $25 OFF in your registration fee has been granted.", formData);
        } else if (VisitorTracking.isWhatsAppChat || VisitorTracking.isSchoolClosed  || VisitorTracking.isOrlandoPromo || VisitorTracking.isTampaPromo) {
            await showSuccessAlert(
                    await translateText("Thank you! Click OK, and you will be redirected to a WhatsApp live chat where one of our representatives will chat with you."), {
                    formData,
                    autoRedirectMs: 5000,
                    onRedirect: async () => {
                        let endpoint = '/api/method/zoni_edu.controller.get_whatsapp_url';
                        if (VisitorTracking.isSchoolClosed) {
                            endpoint = '/api/method/zoni_edu.controller.get_closed_school_whatsapp_url';
                        } else if (VisitorTracking.isOrlandoPromo) {
                            endpoint = '/api/method/zoni_edu.controller.get_orlando_promo_whatsapp_url';
                        } else if (VisitorTracking.isTampaPromo) {
                            endpoint = '/api/method/zoni_edu.controller.get_tampa_promo_whatsapp_url';
                        }
                        let url = await getWpUrl(endpoint, formData);
                        if (url) window.location.href = url;
                    }
                }
            );
        } else if (VisitorTracking.isTelegramChat) {
            await showSuccessAlert(
                    await translateText("Thank you! Click “Go now”, and you will be redirected to a Telegram live chat where one of our representatives will chat with you."), {
                    formData,
                    autoRedirectMs: 5000,
                    onRedirect: async () => {
                        let url = await getWpUrl('/api/method/zoni_edu.controller.get_telegram_url', formData);
                        if (url) window.location.href = url;
                    }
                }
            );
        } else if (VisitorTracking.isLineChat) {
            await showSuccessAlert(
                    await translateText("Thank you! Click “Go now”, and you will be redirected to a Line live chat where one of our representatives will chat with you."), {
                    formData,
                    autoRedirectMs: 5000,
                    onRedirect: async () => {
                        let url = await getWpUrl('/api/method/zoni_edu.controller.get_line_url', formData);
                        if (url) window.location.href = url;
                    }
                }
            );
        } else if (VisitorTracking.isAmericanHighSchoolEnrollment) {
            const supportPhone = $('.header_contact_phone_text').first().text().trim() || '+1-212-736-9000';
            const draftResult = await getZahsApplicationTokenFromContact(formData);

            if (draftResult?.status === 'existing_application_handled') {
                const recoveryTitle = await translateText('Check your email');
                const recoveryMessage = draftResult.message || await translateText(
                    "A previous application exists for that email, we sent a recovery link. If you don't receive the email shortly, please check your spam folder or contact us directly for assistance."
                );
                await showSuccessAlert(recoveryMessage, {
                    formData,
                    title: recoveryTitle,
                });
                return;
            }

            const part1 = await translateText("Thank you! We’ve received your contact information and will redirect you now to complete the full application form.");
            const part2 = await translateText("If you need assistance at any point, please:");
            const list1 = await translateText("Chat or write us by visiting: www.zoni.edu");
            const list2 = await translateText("Call our main phone:");
            const list3 = await translateText("Or reach Zoni American High School directly:");
            const part3 = await translateText("We’re here to help!");

            // Check if supportPhone is different than '14076121747' and adjust the message accordingly
            // remove all not digits for comparison

            list3msg = '';
            if (supportPhone.replace(/\D/g, '') !== '14076121747') {
                list3msg = `<li>${list3} <span style="white-space:nowrap;">+1 (407)&nbsp;612-1747</span></li>`;
            }

            const msg = `
                ${part1}<br/><br/>
                ${part2}
                <ul>
                <li>${list1}</li>
                <li>${list2} <span style="white-space:nowrap;">${supportPhone}</span></li>
                ${list3msg}
                </ul>
                ${part3}
            `;

            await showSuccessAlert(msg, {
                formData,
                autoRedirectMs: 10000, // 10 seconds
                onRedirect: async () => {
                    const redirectLang =
                        formData.url_language_code || document.documentElement.lang || 'en';

                    let url;
                    if (draftResult?.token) {
                        // 2) New behavior: clean redirect with token only
                        url = `/${redirectLang}/highschool/application?token=${encodeURIComponent(draftResult.token)}`;
                    } else {
                        // 3) Fallback: keep the old querystring behavior if something fails
                        const names = await splitName(formData.full_name);
                        url = `/${redirectLang}/highschool/application?`;
                        url += `first_name=${encodeURIComponent(names.first_name || '')}`;
                        url += `&last_name=${encodeURIComponent(names.last_name || '')}`;
                        url += `&middle_name=${encodeURIComponent(names.middle_name || '')}`;
                        url += `&student_email=${encodeURIComponent(formData.email)}`;
                        url += `&student_phone_number=${encodeURIComponent(formData.phone)}`;
                        if (formData.zahs_program_interest) {
                            url += `&program_interest=${encodeURIComponent(formData.zahs_program_interest)}`;
                        }
                        if (formData.zahs_track_interest) {
                            url += `&track_interest=${encodeURIComponent(formData.zahs_track_interest)}`;
                        }
                    }

                    window.location.href = url;
                },
                title: await translateText('Zoni American High School')
            });
        } else if (VisitorTracking.isAmericanHighSchoolAdmissionMeeting) {
            const supportPhone = $('.header_contact_phone_text').first().text().trim() || '+1-212-736-9000';

            const part1 = await translateText("Thank you! We’ve received your contact information and will redirect you now to schedule an admission meeting.");
            const part2 = await translateText("If you need assistance at any point, please:");
            const list1 = await translateText("Chat or write us by visiting: www.zoni.edu");
            const list2 = await translateText("Call our main phone:");
            const list3 = await translateText("Or reach Zoni American High School directly:");
            const part3 = await translateText("We’re here to help!");

            const msg = `
                ${part1}<br/><br/>
                ${part2}
                <ul>
                    <li>${list1}</li>
                    <li>${list2} <span style="white-space:nowrap;">${supportPhone}</span></li>
                    <li>${list3} <span style="white-space:nowrap;">+1 (407)&nbsp;612-1747</span></li>
                </ul>
                ${part3}
            `;

            await showSuccessAlert(msg, {
                formData,
                autoRedirectMs: 10000,
                onRedirect: async () => {
                    let url = await getAppointmentsUrl(formData);
                    if (url) window.location.href = url;
                }
            });
        } else {
            await showSuccessAlert("Thank you for reaching out to Zoni! Your message has been successfully received. One of our team representatives will be in touch with you soon. We appreciate your interest!", formData);
        }
    }

    async function showAlert(message, options = {}) {
    // options:
    // - formData: object (your existing use)
    // - autoRedirectMs: number | null (e.g., 10000 for 10s)
    // - onRedirect: async function | null (called when time is up or "Go now" is clicked)
    // - title: string | null (override modal title)

        const {
            formData = null,
            autoRedirectMs = null,
            onRedirect = null,
            title = null,
            appendHtml = '',
        } = options;

        return new Promise(async (resolve) => {
            message = await Promise.resolve(message);
            // Translate message as before
            message = await translateText(message);
            message = `${message || ''}${appendHtml}`;

            const modalEl = document.getElementById('alertModal');
            if (!modalEl) {
            console.error('Alert modal element not found');
            resolve();
            return;
            }

            // Bootstrap 5 native Modal API
            const bsModal = Modal.getOrCreateInstance(modalEl);

            // Set content + optional title
            document.getElementById('alertModalContent').innerHTML = message;
            if (title) {
            const titleEl = document.getElementById('alertModalLabel');
            if (titleEl) titleEl.textContent = title;
            }

            // Footer elements
            const okBtn          = document.getElementById('alertOkButton');
            const goNowBtn       = document.getElementById('alertGoNowButton');
            const progressWrap   = document.getElementById('alertProgress');
            const progressBar    = document.getElementById('alertProgressBar');
            const countdownWrap  = document.getElementById('alertCountdownWrap');
            const countdownLabel = document.getElementById('alertCountdownSeconds');

            // Reset footer UI
            okBtn.classList.add('d-none');
            goNowBtn.classList.add('d-none');
            progressWrap.classList.add('d-none');
            countdownWrap.classList.add('d-none');

            let wasRedirected = false;
            let intervalId = null;

            // If we should auto-redirect, show the pro UI (countdown + actions)
            if (autoRedirectMs && typeof onRedirect === 'function') {
            goNowBtn.classList.remove('d-none');
            progressWrap.classList.remove('d-none');
            countdownWrap.classList.remove('d-none');

            const total = autoRedirectMs;
            let remaining = total;

            const updateUI = () => {
                const secs = Math.max(0, Math.ceil(remaining / 1000));
                countdownLabel.textContent = String(secs);
                const pct = Math.min(100, Math.round(((total - remaining) / total) * 100));
                progressBar.style.width = pct + '%';
                progressBar.setAttribute('aria-valuenow', String(pct));
            };

            const doRedirect = async () => {
                if (wasRedirected) return;
                wasRedirected = true;
                if (intervalId) clearInterval(intervalId);
                bsModal.hide();              // close the modal before redirect
                try { await onRedirect(); }  // perform the navigation
                catch (e) { console.error(e); }
            };

            // Wire actions
            goNowBtn.onclick = doRedirect;

            // Start countdown
            updateUI();
            intervalId = setInterval(() => {
                remaining -= 100;
                if (remaining <= 0) {
                clearInterval(intervalId);
                doRedirect();
                } else {
                updateUI();
                }
            }, 100);

            // Show the modal after wiring
            bsModal.show();

            // On hidden: if we already redirected, do nothing; otherwise keep your existing behaviors
            modalEl.addEventListener('hidden.bs.modal', async function handler() {
                modalEl.removeEventListener('hidden.bs.modal', handler);
                if (!wasRedirected) {
                // Keep your existing branching when the modal simply closes
                await handleAlertHidden(formData);
                }
                resolve();
            }, { once: true });

            } else {
            // Simple OK-only mode (default behavior you had)
            okBtn.classList.remove('d-none');
            okBtn.onclick = () => {
                bsModal.hide();
            };

            bsModal.show();

            modalEl.addEventListener('hidden.bs.modal', async function handler() {
                modalEl.removeEventListener('hidden.bs.modal', handler);
                // Preserve your existing hidden behavior
                await handleAlertHidden(formData);
                resolve();
            }, { once: true });
            }
        });
    }

    window.showAlert = showAlert;
    
    async function handleAlertHidden(formData) {
        if (enableToursPublicPages && VisitorTracking.isOrderBrochure) {
            window.location.href = 'https://www.zoni.edu/s3/brochures/Zoni_Tours/index.html';
            return;
        }
        if (VisitorTracking.isPartnersPage) {
            window.location.href = '/highschool/partners_page';
            return;
        }
    }

    $('#tour-code-form').on('submit', function (e) {
        e.preventDefault();

        if (!enableToursPublicPages) {
            $('#errorMessage').show();
            return;
        }

        let tourCode = $('#tour_code').val();

        if (tourCode) {
            // Construct the URL with the parameter
            let url = '/api/method/zoni_edu.controller.get_payment_link?code=' + encodeURIComponent(tourCode);

            // Call to Frappe API to verify the tour code using GET
            $.ajax({
                method: 'GET',
                url: url,
                dataType: 'json',
                success: function (data) {
                    if (data.message) {
                        $('#errorMessage').hide();
                        // Set the iframe's src to the Typeform link and open the modal
                        $('#typeformIframe').attr('src', data.message);
                        let myModal = new Modal($('#typeformModal')[0], {});
                        myModal.show();
                    } else {
                        $('#errorMessage').show();
                    }
                },
                error: function (err) {
                    console.error('There was a problem:', err.responseText || err.statusText);
                    $('#errorMessage').show();
                }
            });
        }
    });


    const videoElement = document.querySelector('.video-main');
    if (videoElement) {
        videoElement.addEventListener('canplaythrough', function () {
            // Start playing video only when it's ready to be played
            this.play();
        });
    }

    floatingBarInit();
});

// Hide the alert by default
$("#phone-error").hide();
$("#phone-error-application").hide();
$("#phone-error-guardian").hide();
$("#phone-error-guardian2").hide();

$("#email-error").hide();
$('#email').on('input', function () {
    if (!this.validity.patternMismatch) {
        $("#email-error").hide(); // Hide the alert
    }
});

document.addEventListener('DOMContentLoaded', function () {
    const languageSelector = document.querySelector('.language-selector');

    if(languageSelector) {
        const languageLinks = languageSelector.querySelectorAll('.dropdown-item');
        const dropdownButton = languageSelector.querySelector('#languageDropdownMenuButton');
        const langCodeText = languageSelector.querySelector('.lang-code-text');

        languageLinks.forEach(link => {
            link.addEventListener('click', function (event) {
                event.preventDefault();
                const language = this.getAttribute('data-language');
                const flagClass = this.querySelector('span').className;

                // Update the dropdown button flag
                dropdownButton.innerHTML = `<span class="${flagClass}"></span>`;

                // Update the language code text
                langCodeText.textContent = language;
                Translator.translateContext(language)
            });
        });
    }

    initZahsLastInterest(); // Initialize the Zahs Last Interest module
    initApplicationForm(); // Initialize the application form
    initApplicationPayment(); // Initialize the application payment


    function checkURL(){
        const urlObj = new URL(window.location.href);
        const pathSegments = urlObj.pathname.split('/');

        const filteredSegments = pathSegments.filter(segment => segment.length > 0);

        const secondLastSegment = filteredSegments[filteredSegments.length - 2];
        const thirdLastSegment = filteredSegments[filteredSegments.length - 3];

        if ((secondLastSegment === "tours" && thirdLastSegment !== "zoni") || thirdLastSegment === "job") {
            document.querySelector('.language-selector').classList.add('d-none');
        }
    }
    checkURL();

    // Wait until the DOM has loaded before running the fetchTours function
    // Check the window's width
    if (window.innerWidth > 768) {
        // Only execute fetchTours if the screen width is more than 768
        if (enableToursPublicPages) {
            TourDisplay.fetchTours();
        }
        JobDisplay.fetchJobs();
    }
    else {
        if (enableToursPublicPages) {
            TourDisplay.initializeFilters();
        }
        JobDisplay.initializeFilters();
    }

    // Using debounce on filter changes to prevent excessive calls
    const debouncedFetchTours = Utils.debounce(TourDisplay.fetchTours, 250);
    const debouncedFetchJobs = Utils.debounce(JobDisplay.fetchJobs, 250);

    // Event listeners for Tours dropdowns
    let filterCategoryElem = $('#filter-category');
    if (filterCategoryElem.length) {
        filterCategoryElem.on('select2:select select2:unselect', function () {
            if (!enableToursPublicPages) {
                return;
            }
            $(this).val($(this).val()).trigger('change'); // Force update value
            debouncedFetchTours();
        });
    }

    let filterDestinationElem = $('#filter-destination');
    if (filterDestinationElem.length) {
        filterDestinationElem.on('select2:select select2:unselect', function () {
            if (!enableToursPublicPages) {
                return;
            }
            $(this).val($(this).val()).trigger('change'); // Force update value
            debouncedFetchTours();
        });
    }

    let filterDaysElem = $('#filter-days');
    if (filterDaysElem.length) {
        filterDaysElem.on('select2:select select2:unselect', function () {
            if (!enableToursPublicPages) {
                return;
            }
            $(this).val($(this).val()).trigger('change'); // Force update value
            debouncedFetchTours();
        });
    }

    // Event listeners for Careers dropdowns
    let filterLocationElem = $('#filter-location');
    if (filterLocationElem.length) {
        filterLocationElem.on('select2:select select2:unselect', function () {
            $(this).val($(this).val()).trigger('change'); // Force update value
            debouncedFetchJobs();
        });
    }

    let filterTypeElem = $('#filter-type');
    if (filterTypeElem.length) {
        filterTypeElem.on('select2:select select2:unselect', function () {
            $(this).val($(this).val()).trigger('change'); // Force update value
            debouncedFetchJobs();
        });
    }

    let filterTitleElem = $('#filter-title');
    if (filterTitleElem.length) {
        filterTitleElem.on('select2:select select2:unselect', function () {
            $(this).val($(this).val()).trigger('change'); // Force update value
            debouncedFetchJobs();
        });
    }

    // $("#dropdown-menu").select2({ theme: 'bootstrap-5' });
    function searchFilter() {
        var input, filter, ul, li, a, i, txtValue;
        input = document.getElementById("myInput");
        filter = input.value.toUpperCase();
        ul = document.getElementById("dropdown-menu");
        li = ul.getElementsByTagName("li");
        for (i = 0; i < li.length; i++) {
            a = li[i].getElementsByTagName("a")[0];
            txtValue = a.textContent || a.innerText;
            if (txtValue.toUpperCase().indexOf(filter) > -1) {
                li[i].style.display = "";
            } else {
                li[i].style.display = "none";
            }
        }
    }
    $("#myInput").on("keyup", function () {
        searchFilter();
    });

    Scroll.scrollToLeftById('#school-programs-left-arrow-id', '#school-programs-card-container');
    Scroll.scrollToRightById('#school-programs-right-arrow-id', '#school-programs-card-container');

    ResizeListenUpdates.transformYearsProgramSectionByResize();

    var paymentCodeLink = "{{ context.payment_code_link }}";

    $('#openBookingTourModalBtn').click(function(event) {
        event.preventDefault(); // Prevent the default action
        var paymentCodeLink = $(this).data('payment-code-link'); // Get the data attribute
        $('#typeformIframe').attr('src', paymentCodeLink); // Set the iframe src to the payment code link
        var modal = new Modal(document.getElementById('typeformModalTourBooking')); // Initialize the modal
        modal.show(); // Show the modal
    });


    EnrollNow.initializeEnrollNow();
    Schedule.initializeSchedule();
    Translator.updateLanguageDropdown();

    //Show the custom toast message
    var toastEl = document.getElementById('customToast');
    if (
        toastEl &&
        window.innerWidth >= 768 &&
        !isTourBookingLink &&
        !isCareersPage &&
        !isZahsPage &&
        !disableNewsToast &&
        VisitorTracking.isFirstTimeVisitor()
    ) {
        var toast = new Toast(toastEl, { delay: 300000 });
        toast.show();
    }

    StoreProducts.loadProducts();
    initBlogSlider();

    // Fullscreen functionality on Zoni Careers page
    initAwardsImageModal();
    initChatPlatformBadge();
});

if (enableToursPublicPages) {
    window.addEventListener('resize', Utils.debounce(TourDisplay.fetchTours, 250));
}
window.addEventListener('resize', Utils.debounce(JobDisplay.fetchJobs, 250));

async function splitName(fullName) {
  const res = await fetch(`/api/method/zoni_edu.services.split_name?full_name=${fullName}`);
  const { message } = await res.json();
  return message;
}

async function getWpUrl(endpoint, formData) {
    const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Frappe-CSRF-Token': frappe.csrf_token,
        },
        credentials: 'include',
        body: JSON.stringify({
            message: formData.message,
            phone: formData.phone,
            email: formData.email,
            name: formData.full_name,
        }),
    });
    const { message } = await res.json();
    return message;
}

async function getAppointmentsUrl(formData) {
    const apiUrl = '/api/method/zoni_edu.controller.get_appointment_url'
        + '?name='    + encodeURIComponent(formData.full_name)
        + '&phone='   + encodeURIComponent(formData.phone)
        + '&email='   + encodeURIComponent(formData.email);
        
    const res = await fetch(apiUrl);
    const { message } = await res.json();
    return message;
}


async function initAwardsImageModal() {
  const carousel = document.getElementById('carouselawardslg');
  const modalEl  = document.getElementById('awardImageModal');
  const modalImg = document.getElementById('awardImageModalImg');

  if (!carousel || !modalEl || !modalImg) return;

  // Select only the <img> tags inside your specific carousel
  carousel.querySelectorAll('.carousel-item img').forEach(img => {
    img.style.cursor = 'pointer';
    img.addEventListener('click', () => {
      // Populate & show the modal
      modalImg.src = img.src;
      new Modal(modalEl).show();
    });
  });
}
